/**
 * Utilitaires pour identifier le type d'utilisateur (client vs collaborateur)
 */

import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

/**
 * Vérifie si un utilisateur est un bénéficiaire (nouveau système)
 * @param {object} db - Instance Firestore
 * @param {string} userId - ID de l'utilisateur
 * @returns {Promise<boolean>} True si c'est un bénéficiaire, false sinon
 */
export const isBeneficiaryUser = async (db, userId) => {
  if (!db || !userId) {
    console.log('isBeneficiaryUser: missing db or userId');
    return false;
  }

  try {
    const employeeDoc = await getDoc(doc(db, 'employees', userId));
    if (employeeDoc.exists()) {
      const role = employeeDoc.data().role;
      const isBeneficiary = role === 'beneficiaire';
      console.log(`isBeneficiaryUser: userId="${userId}", role="${role}", result=${isBeneficiary}`);
      return isBeneficiary;
    }
    return false;
  } catch (error) {
    console.error('Erreur lors de la vérification bénéficiaire:', error);
    return false;
  }
};

/**
 * Vérifie si un utilisateur est un client basé sur son email (ancienne méthode, gardée pour compatibilité)
 * @param {string} email - Email de l'utilisateur
 * @returns {boolean} True si c'est un client, false sinon
 */
export const isClientUser = (email) => {
  if (!email) {
    console.log('isClientUser: no email provided');
    return false;
  }

  const isClient = email.endsWith('@agentime.fr') && email.startsWith('client_');
  console.log(`isClientUser: email="${email}", endsWith="@agentime.fr"=${email.endsWith('@agentime.fr')}, startsWith="client_"=${email.startsWith('client_')}, result=${isClient}`);

  return isClient;
};

/**
 * Vérifie si un utilisateur est un collaborateur (employé)
 * @param {object} db - Instance Firestore
 * @param {string} userId - ID de l'utilisateur
 * @returns {Promise<boolean>} True si c'est un collaborateur, false sinon
 */
export const isEmployeeUser = async (db, userId) => {
  if (!userId) return false;
  
  try {
    const employeeDoc = await getDoc(doc(db, 'employees', userId));
    return employeeDoc.exists();
  } catch (error) {
    console.error('Erreur lors de la vérification employé:', error);
    return false;
  }
};

/**
 * Récupère l'ID du client à partir de l'email client (ancienne méthode)
 * @param {string} email - Email du client
 * @returns {string|null} ID du client ou null si non trouvé
 */
export const getClientIdFromEmail = (email) => {
  if (!isClientUser(email)) return null;

  // Méthode simple comme dans ClientLogin.js
  // Préserver la casse originale de l'ID client
  const clientPart = email.split('@')[0]; // client_E4EqCGMsoOiLGNRkXoL5
  return clientPart.replace('client_', ''); // E4EqCGMsoOiLGNRkXoL5
};

/**
 * Récupère l'ID du client associé à un bénéficiaire (nouveau système)
 * @param {object} db - Instance Firestore
 * @param {string} beneficiaryId - ID du bénéficiaire (employé avec rôle beneficiaire)
 * @returns {Promise<string|null>} ID du client ou null si non trouvé
 */
export const getClientIdFromBeneficiary = async (db, beneficiaryId) => {
  if (!db || !beneficiaryId) {
    console.log('getClientIdFromBeneficiary: missing db or beneficiaryId');
    return null;
  }

  try {
    // Chercher dans l'enregistrement employee le champ clientId
    const employeeDoc = await getDoc(doc(db, 'employees', beneficiaryId));

    if (employeeDoc.exists()) {
      const employeeData = employeeDoc.data();
      const clientId = employeeData.clientId;

      if (clientId) {
        console.log(`getClientIdFromBeneficiary: found clientId ${clientId} for beneficiary ${beneficiaryId}`);
        return clientId;
      } else {
        console.log(`getClientIdFromBeneficiary: no clientId field in employee ${beneficiaryId}`);
      }
    } else {
      console.log(`getClientIdFromBeneficiary: employee ${beneficiaryId} not found`);
    }

    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération du client:', error);
    return null;
  }
};

/**
 * Vérifie si un utilisateur a accès à une route d'administration
 * @param {object} db - Instance Firestore
 * @param {object} user - Utilisateur Firebase
 * @returns {Promise<boolean>} True si accès autorisé, false sinon
 */
export const hasAdminAccess = async (db, user) => {
  if (!user || !user.email) return false;

  // Les clients (ancienne méthode) n'ont pas accès à l'administration
  if (isClientUser(user.email)) {
    return false;
  }

  // Les bénéficiaires (nouveau système) n'ont pas accès à l'administration
  if (db && user.uid) {
    const isBeneficiary = await isBeneficiaryUser(db, user.uid);
    if (isBeneficiary) {
      console.log('hasAdminAccess: Beneficiary user, access denied');
      return false;
    }
  }

  // Vérifier si c'est un employé avec les permissions appropriées
  try {
    const employeeDoc = await getDoc(doc(db, 'employees', user.uid));
    if (employeeDoc.exists()) {
      const employeeData = employeeDoc.data();
      // Vérifier si l'employé n'est pas archivé et n'est pas un bénéficiaire
      return !employeeData.isArchived && employeeData.role !== 'beneficiaire';
    }
    return false;
  } catch (error) {
    console.error('Erreur lors de la vérification des permissions admin:', error);
    return false;
  }
};

/**
 * Détermine la route de redirection appropriée pour un utilisateur connecté
 * @param {object} db - Instance Firestore
 * @param {object} user - Utilisateur Firebase
 * @returns {Promise<string>} Route de redirection
 */
export const getRedirectPath = async (db, user) => {
  if (!user || !user.email) {
    console.log('getRedirectPath: No user or email provided');
    return '/';
  }

  console.log('getRedirectPath - User email:', user.email, 'UID:', user.uid);

  try {
    // Vérifier d'abord si c'est un bénéficiaire (nouveau système)
    if (db && user.uid) {
      const isBeneficiary = await isBeneficiaryUser(db, user.uid);
      if (isBeneficiary) {
        const clientId = await getClientIdFromBeneficiary(db, user.uid);
        if (clientId) {
          console.log('Redirecting beneficiary to client dashboard:', clientId);
          return `/client-dashboard/${clientId}`;
        } else {
          console.warn('Beneficiary found but no associated client');
        }
      }
    }

    // Vérifier l'ancienne méthode (email avec préfixe client_)
    if (isClientUser(user.email)) {
      const clientId = getClientIdFromEmail(user.email);
      console.log('Client ID extracted from email:', clientId);

      // Vérifier que l'ID client est valide
      if (!clientId || clientId.length < 5) {
        console.error('Invalid client ID extracted:', clientId);
        return '/';
      }

      return `/client-dashboard/${clientId}`;
    }

    console.log('Redirecting to employee dashboard');
    // Par défaut, rediriger vers le dashboard des employés
    return '/dashboard';
  } catch (error) {
    console.error('Error in getRedirectPath:', error);
    return '/';
  }
};
